/**
 * SortOptionsListView - Collapsible sort options for mobile modal context
 *
 * Shows a trigger displaying the selected sort value. Tapping the trigger
 * expands the full options list. Selecting an option collapses the list.
 * Replaces the GlueStack Menu popover which gets clipped by ScrollView
 * overflow boundaries inside FullScreenOverlay modals.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import {
  ArrowDownAZ,
  ArrowUpZA,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react-native";
import React, { memo } from "react";

import {
  SortFieldCallbackMap,
  SimilarPlayersSortOption,
} from "@/features/player-detail/types";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { SimilarPlayerSortField, SortOrder } from "@/generated/graphql";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Props for SortOptionsListView component
 */
interface SortOptionsListViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Available sort options */
  readonly sortOptions: readonly SimilarPlayersSortOption[];
  /** Currently selected sort field */
  readonly selectedSortField: SimilarPlayerSortField;
  /** Display label for selected sort option */
  readonly selectedSortLabel: string;
  /** Current sort order */
  readonly sortOrder: SortOrder;
  /** Pre-bound callbacks for each sort field */
  readonly sortFieldCallbacks: SortFieldCallbackMap;
  /** Callback to toggle sort order */
  readonly onSortOrderToggle: () => void;
  /** Whether the options list is expanded */
  readonly isExpanded: boolean;
  /** Callback to toggle expanded/collapsed state */
  readonly onToggleExpanded: () => void;
}

/**
 * Renders a collapsible sort options list with trigger and sort order toggle
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.sortOptions - Available sort options
 * @param props.selectedSortField - Currently selected sort field
 * @param props.selectedSortLabel - Display label for selected sort option
 * @param props.sortOrder - Current sort order
 * @param props.sortFieldCallbacks - Pre-bound callbacks for each sort field
 * @param props.onSortOrderToggle - Callback to toggle sort order
 * @param props.isExpanded - Whether the options list is expanded
 * @param props.onToggleExpanded - Callback to toggle expanded/collapsed state
 * @returns HStack with collapsible sort options and order toggle
 */
const SortOptionsListView = ({
  sortOptions,
  selectedSortField,
  selectedSortLabel,
  sortOrder,
  sortFieldCallbacks,
  onSortOrderToggle,
  isExpanded,
  onToggleExpanded,
}: SortOptionsListViewProps) => (
  <Stack className="gap-2">
    <Stack direction="horizontal" className="gap-2">
      <Pressable
        onPress={onToggleExpanded}
        testID={TEST_ID_PLAYER_DETAIL.SP_SORT_DROPDOWN}
        className="h-10 flex-1"
      >
        <Stack
          direction="horizontal"
          className="h-full flex-1 items-center justify-between rounded-none border border-outline-strong bg-surface-raised px-3"
        >
          <Text
            variant="unstyled"
            className="text-title-sm text-content-primary"
          >
            {selectedSortLabel}
          </Text>
          <Icon
            as={isExpanded ? ChevronUp : ChevronDown}
            size="sm"
            className="text-content-primary"
          />
        </Stack>
      </Pressable>
      <Pressable
        onPress={onSortOrderToggle}
        className="size-10 items-center justify-center rounded-none border border-outline-strong bg-surface-raised active:bg-surface-subtle"
      >
        <Icon
          as={sortOrder === SortOrder.Desc ? ArrowDownAZ : ArrowUpZA}
          size="sm"
          className="text-content-primary"
        />
      </Pressable>
    </Stack>
    {isExpanded && (
      <Stack className="overflow-hidden rounded-sm border border-outline-subtle bg-surface-raised p-1">
        {sortOptions.map(option => {
          const isSelected = selectedSortField === option.field;
          return (
            <Pressable
              key={option.id}
              onPress={sortFieldCallbacks[option.field]}
              testID={`${TEST_ID_PLAYER_DETAIL.SP_SORT_OPTION_PREFIX}${option.field}`}
              className={`rounded-none px-3 py-2 ${
                isSelected ? "bg-surface-subtle" : ""
              }`}
            >
              <Stack
                direction="horizontal"
                className="flex-1 items-center justify-between"
              >
                <Text variant="unstyled" className="text-content-primary">
                  {option.label}
                </Text>
                {isSelected && (
                  <Icon
                    as={Check}
                    size="sm"
                    className="text-needs-semantic-primary-500"
                  />
                )}
              </Stack>
            </Pressable>
          );
        })}
      </Stack>
    )}
  </Stack>
);

SortOptionsListView.displayName = "SortOptionsListView";

export default memo(SortOptionsListView);
